// FormField — label + control + optional description + optional error.
// Owns the field accessibility contract end to end: it is the single
// source of truth for the control id (always injected from htmlFor,
// overriding whatever the child had), and it clones the child to
// merge aria-describedby (description + error ids), aria-invalid when
// error is set, and required when the prop is set.

import { Children, cloneElement } from 'react'
import type { ReactElement } from 'react'

export interface FormFieldProps {
  label: string
  htmlFor: string
  description?: string
  error?: string
  required?: boolean
  children: ReactElement<Record<string, unknown>>
}

export function FormField({
  label,
  htmlFor,
  description,
  error,
  required = false,
  children,
}: FormFieldProps) {
  const descriptionId = description !== undefined ? `${htmlFor}-description` : undefined
  const errorId = error !== undefined ? `${htmlFor}-error` : undefined

  const child = Children.only(children)
  const existingDescribedBy =
    typeof child.props['aria-describedby'] === 'string'
      ? (child.props['aria-describedby'] as string)
      : undefined
  const describedBy = [existingDescribedBy, descriptionId, errorId]
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .join(' ')

  const mergedProps: Record<string, unknown> = { id: htmlFor }
  if (describedBy.length > 0) mergedProps['aria-describedby'] = describedBy
  if (error !== undefined) mergedProps['aria-invalid'] = true
  if (required) mergedProps.required = true
  const control = cloneElement(child, mergedProps)

  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-800">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-1 text-red-600">
            *
          </span>
        ) : null}
      </label>
      {control}
      {description !== undefined ? (
        <p id={descriptionId} className="text-xs text-gray-500">
          {description}
        </p>
      ) : null}
      {error !== undefined ? (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  )
}
